import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Calendar, MapPin, Clock, CalendarPlus, Sun, Cloud, CloudRain, Sparkles } from 'lucide-react';
import Countdown from '../Match/Countdown';
import { getTeamLogo, getTeamInitial } from '../../utils/teamLogos';

// Mini compteur pour mobile
function MiniCountdown({ targetDate }) {
  const [remaining, setRemaining] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const target = new Date(targetDate);
      const diff = target - now;

      if (diff <= 0) {
        setRemaining("LIVE");
        clearInterval(interval);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / 1000 / 60) % 60);

      // Format compact
      if (days > 0) {
        setRemaining(`${days}j ${hours}h`);
      } else if (hours > 0) {
        setRemaining(`${hours}h ${minutes}m`);
      } else {
        setRemaining(`${minutes}m`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return (
    <div className="flex items-center gap-1 bg-gradient-to-r from-[#FFE400]/20 to-[#BDFF00]/20 rounded-full px-2 py-0.5 border border-[#FFE400]/30">
      <Clock className="w-3 h-3 text-[#FFE400]" />
      <span className="text-[#FFE400] text-xs font-bold">{remaining}</span>
    </div>
  );
}

export default function ProchainMatchBar({ match }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Détection du scroll pour réduire l'élément
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 100); // Réduit après 100px de scroll
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fonction pour générer le lien Google Calendar
  const generateGoogleCalendarUrl = () => {
    if (!match) return '';

    const title = `${match.home.short_name_federation} vs ${match.away.short_name_federation}`;
    const startDate = new Date(`${match.date?.split('T')[0]}T${match.time?.replace('H', ':')}`);
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // +2h

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const location = `${match.terrain?.name || ''}, ${match.terrain?.city || ''}`;
    const details = `Match de ${match.competition?.name || 'Football'} - ${match.poule?.name || ''}`;

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
      location: location,
      details: details
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Fonction pour obtenir une icône météo aléatoire (simulation)
  const getWeatherIcon = () => {
    const icons = [
      { Icon: Sun, temp: '16°C', color: 'text-yellow-400' },
      { Icon: Cloud, temp: '14°C', color: 'text-gray-300' },
      { Icon: CloudRain, temp: '12°C', color: 'text-blue-300' }
    ];
    const weather = icons[Math.floor(Math.random() * icons.length)];
    return weather;
  };

  const weather = getWeatherIcon();

  if (!match) return null;

  return (
    <div
      className="fixed left-1/2 transform -translate-x-1/2 w-[90vw] max-w-2xl flex flex-col items-center z-[60]"
      style={{ top: '80px' }}
    >
      {/* Barre principale - GLASSMORPHISM avec couleurs EFAFC */}
      <motion.button
        whileHover={{ scale: scrolled ? 1 : 1.02, y: scrolled ? 0 : -2 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        animate={{
          width: scrolled ? '280px' : '100%',
          paddingLeft: scrolled ? '12px' : '16px',
          paddingRight: scrolled ? '12px' : '16px',
        }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="relative overflow-hidden rounded-2xl backdrop-blur-md bg-gradient-to-r from-[#001B56]/90 via-[#002a7a]/90 to-[#001B56]/90 border border-white/20 shadow-2xl group py-3"
      >
        {/* Effet de brillance animé */}
        <motion.div
          animate={{
            x: [-1000, 1000],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 w-[200%] h-full bg-gradient-to-r from-transparent via-white/10 to-transparent"
        />

        <div className="relative flex items-center justify-center sm:justify-between gap-2 sm:gap-3">
          {/* Section : Icône + Titre (desktop) + Logos + Décompte (mobile) */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 min-w-0">
            <Sparkles className="text-[#FFE400] w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />

            {/* Titre "PROCHAIN MATCH" - masqué sur mobile */}
            <span className="hidden sm:inline text-white font-extrabold text-sm sm:text-lg tracking-wider whitespace-nowrap">
              PROCHAIN MATCH
            </span>

            {/* Logos des équipes - TOUJOURS visibles sur mobile, cachés quand scrollé sur desktop */}
            <motion.div
              animate={{
                opacity: scrolled ? (window.innerWidth < 640 ? 1 : 0) : 1,
                width: scrolled ? (window.innerWidth < 640 ? 'auto' : 0) : 'auto',
              }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-1 sm:gap-2 overflow-hidden"
            >
              {/* Logo domicile */}
              {getTeamLogo(match.home.short_name_federation) ? (
                <img
                  src={getTeamLogo(match.home.short_name_federation)}
                  alt={match.home.short_name_federation}
                  className="h-6 w-6 sm:h-8 sm:w-8 object-contain drop-shadow-lg"
                />
              ) : (
                <div className="h-6 w-6 sm:h-8 sm:w-8 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center">
                  <span className="text-[#001B56] font-bold text-[10px] sm:text-xs">
                    {getTeamInitial(match.home.short_name_federation).initial}
                  </span>
                </div>
              )}

              {/* Badge VS mini */}
              <div className="h-4 w-4 sm:h-6 sm:w-6 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center">
                <span className="text-[#001B56] font-black text-[8px] sm:text-xs">VS</span>
              </div>

              {/* Logo extérieur */}
              {getTeamLogo(match.away.short_name_federation) ? (
                <img
                  src={getTeamLogo(match.away.short_name_federation)}
                  alt={match.away.short_name_federation}
                  className="h-6 w-6 sm:h-8 sm:w-8 object-contain drop-shadow-lg"
                />
              ) : (
                <div className="h-6 w-6 sm:h-8 sm:w-8 bg-gradient-to-br from-white/20 to-white/10 rounded-full flex items-center justify-center border border-white/30">
                  <span className="text-white font-bold text-[10px] sm:text-xs">
                    {getTeamInitial(match.away.short_name_federation).initial}
                  </span>
                </div>
              )}
            </motion.div>

            {/* Mini décompte - UNIQUEMENT sur mobile, TOUJOURS visible */}
            <div className="flex sm:hidden items-center ml-1">
              <MiniCountdown targetDate={`${match.date?.split('T')[0]}T${match.time?.replace('H', ':')}`} />
            </div>
          </div>

          {/* Section centrale : Infos rapides - visibles uniquement sur desktop quand non scrollé */}
          <motion.div
            animate={{
              opacity: scrolled ? 0 : 1,
              width: scrolled ? 0 : 'auto',
            }}
            transition={{ duration: 0.3 }}
            className="hidden sm:flex items-center gap-4 overflow-hidden"
          >
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1">
              <Clock className="w-4 h-4 text-[#BDFF00]" />
              <span className="text-white text-sm font-medium">{match.time}</span>
            </div>
            <div className="text-[#FFE400] text-sm font-bold bg-white/10 rounded-full px-3 py-1">
              {match.competition?.name}
            </div>
          </motion.div>

          {/* Chevron avec rotation */}
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="flex-shrink-0"
          >
            <ChevronDown className="w-6 h-6 text-[#FFE400]" />
          </motion.div>
        </div>

        {/* Barre de séparation jaune au bas */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#FFE400] via-[#BDFF00] to-[#FFE400]" />
      </motion.button>

      {/* Panneau déployable VERS LE BAS avec GLASSMORPHISM */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0, y: -20 }}
            animate={{ height: 'auto', opacity: 1, y: 0 }}
            exit={{ height: 0, opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="w-full overflow-hidden"
          >
            <div className="backdrop-blur-xl bg-gradient-to-br from-[#001B56]/95 via-[#002a7a]/95 to-[#001B56]/95 border-x border-b border-white/20 rounded-b-2xl shadow-2xl">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                className="p-6 space-y-6"
              >
                {/* En-tête avec météo et journée */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2 backdrop-blur-sm">
                    <weather.Icon className={`w-5 h-5 ${weather.color}`} />
                    <span className="text-white text-sm font-medium">{weather.temp}</span>
                  </div>
                  <div className="text-[#BDFF00] text-sm font-bold bg-white/10 rounded-xl px-4 py-2">
                    {match.poule?.name || 'N2'}
                  </div>
                </div>

                {/* Section VS avec logos */}
                <div className="flex items-center justify-center gap-6 sm:gap-12">
                  {/* Équipe domicile */}
                  <motion.div
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="flex flex-col items-center gap-3 group"
                  >
                    <div className="relative">
                      {getTeamLogo(match.home.short_name_federation) ? (
                        <img
                          src={getTeamLogo(match.home.short_name_federation)}
                          alt={match.home.short_name_federation}
                          className="h-16 sm:h-20 w-16 sm:w-20 object-contain drop-shadow-2xl transition-transform group-hover:scale-110"
                        />
                      ) : (
                        <div className="h-16 sm:h-20 w-16 sm:w-20 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl flex items-center justify-center shadow-lg">
                          <span className="text-[#001B56] font-black text-2xl">
                            {getTeamInitial(match.home.short_name_federation).initial}
                          </span>
                        </div>
                      )}
                      {/* Indicateur "Domicile" */}
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#FFE400] text-[#001B56] text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                        DOMICILE
                      </div>
                    </div>
                    <span className="text-white text-xs sm:text-sm font-bold text-center max-w-[100px] leading-tight mt-2">
                      {match.home.short_name_federation}
                    </span>
                  </motion.div>

                  {/* VS Badge */}
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-full flex items-center justify-center shadow-2xl">
                    <span className="text-[#001B56] font-black text-xl sm:text-2xl">VS</span>
                  </div>

                  {/* Équipe extérieur */}
                  <motion.div
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="flex flex-col items-center gap-3 group"
                  >
                    <div className="relative">
                      {getTeamLogo(match.away.short_name_federation) ? (
                        <img
                          src={getTeamLogo(match.away.short_name_federation)}
                          alt={match.away.short_name_federation}
                          className="h-16 sm:h-20 w-16 sm:w-20 object-contain drop-shadow-2xl transition-transform group-hover:scale-110"
                        />
                      ) : (
                        <div className="h-16 sm:h-20 w-16 sm:w-20 bg-gradient-to-br from-white/20 to-white/10 rounded-2xl flex items-center justify-center shadow-lg border border-white/30">
                          <span className="text-white font-black text-2xl">
                            {getTeamInitial(match.away.short_name_federation).initial}
                          </span>
                        </div>
                      )}
                      {/* Indicateur "Extérieur" */}
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap border border-white/30">
                        EXTÉRIEUR
                      </div>
                    </div>
                    <span className="text-white text-xs sm:text-sm font-bold text-center max-w-[100px] leading-tight mt-2">
                      {match.away.short_name_federation}
                    </span>
                  </motion.div>
                </div>

                {/* Informations du match - GLASSMORPHISM */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#FFE400]" />
                      <div className="flex flex-col">
                        <span className="text-white text-xs font-medium">
                          {match.date ? new Date(match.date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long'
                          }) : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#BDFF00]" />
                      <span className="text-white text-xs font-medium">
                        Coup d'envoi {match.time}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 sm:col-span-1 col-span-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#FFE400]" />
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-white text-xs font-medium truncate">
                          {match.terrain?.name || 'Stade'}
                        </span>
                        <span className="text-[#93C5FD] text-xs truncate">
                          {match.terrain?.city || ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compte à rebours avec style premium */}
                <div className="bg-gradient-to-r from-white/5 via-white/10 to-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <Countdown targetDate={`${match.date?.split('T')[0]}T${match.time?.replace('H', ':')}`} />
                </div>

                {/* Bouton CTA - Style EFAFC */}
                <motion.a
                  href={generateGoogleCalendarUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] px-6 py-4 rounded-2xl font-bold text-sm shadow-xl hover:shadow-2xl transition-all duration-300 group"
                >
                  <CalendarPlus className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span className="tracking-wide">AJOUTER À MON CALENDRIER</span>
                </motion.a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 